import { FieldError, IconButton } from "@anna-money/anna-web-ui";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import type { ChangeEvent, FC, ReactElement } from "react";
import { useState } from "react";
import { Form } from "../../../../components/form/form";
import { Input } from "../../../../components/input/input";
import { Toggle } from "../../../../components/toggle/toggle";
import { useFactory } from "../../../../helpers/reactHooks";
import { EmailIcon, PhoneIcon, SiteIcon } from "../../../../icons/icons";
import type { PaymentPageForm } from "../../../../types/paymentPage";
import type { FormSectionType } from "../../obPaymentPageForm";
import { contactsDetailsValidationConfig } from "../../validation";
import { EditFormModalSection } from "../editFormSection/editFormModalSection";
import styles from "./contactDetailsSection.module.scss";

type ContactDetailsForm = Pick<
  PaymentPageForm,
  "contactWebsite" | "contactPhone" | "contactEmail" | "switchedOnContactsDetails"
>;

// Both the page form and the modal form are edited through renderForm. Form<T> is invariant in T,
// so describe only the part renderForm uses; method syntax keeps the parameters bivariant.
/* eslint-disable @typescript-eslint/method-signature-style -- property syntax would make Form<PaymentPageForm> not fit */
type ContactDetailsFields = {
  getValue<K extends keyof ContactDetailsForm>(field: K): ContactDetailsForm[K];
  onChange<K extends keyof ContactDetailsForm>(field: K, value: ContactDetailsForm[K]): void;
  getError(field: string): string | undefined;
};
/* eslint-enable @typescript-eslint/method-signature-style */

export const ContactDetailsSection: FC<FormSectionType> = observer(({ form, store }) => {
  const { contactWebsite, contactPhone, contactEmail, switchedOnContactsDetails } = form.values;
  const modalForm = useFactory(
    () =>
      new Form<ContactDetailsForm>(
        { contactWebsite, contactPhone, contactEmail, switchedOnContactsDetails },
        contactsDetailsValidationConfig,
      ),
  );
  const [isFilled, setFilled] = useState(!!contactWebsite || !!contactPhone || !!contactEmail);
  const [editMode, setEditMode] = useState(!isFilled);

  const renderViewContent = (): ReactElement => (
    <div className={styles.viewSection}>
      <div className={styles.contactDetailsSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionViewTitle}>
            <span className={styles.sectionSubtitle}>Contact details</span>
          </div>

          <IconButton
            title="Edit contact details"
            icon="actionEdit"
            iconColor="themeGreen"
            size="s"
            onClick={() => {
              setEditMode(true);
            }}
          />
        </div>
        <div className={styles.sectionViewContent}>
          {contactPhone && (
            <div className={styles.rowContent}>
              <PhoneIcon width="17" height="17" />
              <span className={styles.text}>{contactPhone}</span>
            </div>
          )}
          {contactEmail && (
            <div className={styles.rowContent}>
              <EmailIcon width="17" height="17" />
              <span className={styles.text}>{contactEmail}</span>
            </div>
          )}
          {contactWebsite && (
            <div className={styles.rowContent}>
              <SiteIcon width="17" height="17" />
              <span className={styles.text}>{contactWebsite}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const handleChangeToggle = (value: boolean, formData: ContactDetailsFields): void => {
    formData.onChange("switchedOnContactsDetails", value);
    if (!value) {
      formData.onChange("contactWebsite", "");
      formData.onChange("contactPhone", "");
      formData.onChange("contactEmail", "");
    }
  };

  const renderForm = (formData: ContactDetailsFields): ReactElement => (
    <div className={styles.amountSection}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <span className={styles.sectionSubtitle}>Contact details</span>
          <span className={styles.sectionDescription}>Include ways of how your customer can reach you</span>
        </div>
        <Toggle
          label="Contact details"
          value={formData.getValue("switchedOnContactsDetails")}
          onChange={(value) => {
            handleChangeToggle(value, formData);
          }}
        />
      </div>

      {formData.getValue("switchedOnContactsDetails") && (
        <>
          <Input
            label="Phone"
            className={styles.field}
            placeholder="+44"
            id="contactPhone"
            autoCapitalize="none"
            error={store.getValidationError("contactPhone")}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              formData.onChange("contactPhone", e.target.value);
              store.clearValidationError("contactPhone");
            }}
            value={formData.getValue("contactPhone") ?? undefined}
          />
          <Input
            label="Email"
            className={styles.field}
            placeholder="yourbusiness@email.com"
            id="contactEmail"
            autoCapitalize="none"
            error={formData.getError("contactEmail") || store.getValidationError("contactEmail")}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              formData.onChange("contactEmail", e.target.value);
              store.clearValidationError("contactEmail");
            }}
            value={formData.getValue("contactEmail") ?? undefined}
          />

          <Input
            label="Website"
            className={styles.field}
            placeholder="https://webpage.com"
            id="contactWebsite"
            autoCapitalize="none"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              formData.onChange("contactWebsite", e.target.value);
              store.clearValidationError("contactWebsite");
            }}
            value={formData.getValue("contactWebsite") ?? undefined}
          />
          <FieldError
            errorText={formData.getError("contactWebsite") || store.getValidationError("contactWebsite") || ""}
          />
        </>
      )}
    </div>
  );

  if (store.firstUse || !isFilled) {
    return (
      <div className={classnames(styles.section, styles.withSeparator)}>
        <div>{renderForm(form)}</div>
      </div>
    );
  }

  const handleCancelEditModal = (): void => {
    setEditMode(false);
    modalForm.onMultipleChange([
      { field: "contactWebsite", value: form.getValue("contactWebsite") },
      { field: "contactPhone", value: form.getValue("contactPhone") },
      { field: "contactEmail", value: form.getValue("contactEmail") },
      { field: "switchedOnContactsDetails", value: form.getValue("switchedOnContactsDetails") },
    ]);
  };

  const handleSaveEditModal = (): void => {
    if (!modalForm.isValid()) {
      return;
    }
    setEditMode(false);
    if (
      [
        modalForm.getValue("contactWebsite"),
        modalForm.getValue("contactPhone"),
        modalForm.getValue("contactEmail"),
      ].some(Boolean)
    ) {
      setFilled(true);
    } else {
      form.onChange("switchedOnContactsDetails", false);
      setFilled(false);
    }
    form.onMultipleChange([
      { field: "contactWebsite", value: modalForm.getValue("contactWebsite") },
      { field: "contactPhone", value: modalForm.getValue("contactPhone") },
      { field: "contactEmail", value: modalForm.getValue("contactEmail") },
      { field: "switchedOnContactsDetails", value: modalForm.getValue("switchedOnContactsDetails") },
    ]);
  };

  return (
    <>
      {renderViewContent()}
      <EditFormModalSection open={editMode} onCancel={handleCancelEditModal} onSave={handleSaveEditModal}>
        <div className={styles.section}>{renderForm(modalForm)}</div>
      </EditFormModalSection>
    </>
  );
});
